import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy-deep">
      <div className="relative hidden lg:block grain overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1558661091-5cc1b64d0dc5"
          alt="UYI"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <span className="flex h-16 w-16 items-center justify-center rounded-sm bg-white/95 p-1.5">
            <img src="/uyi-logo.png" alt="United Youngs India logo" className="h-full w-full object-contain" />
          </span>
          <div>
            <h1 className="font-display text-4xl leading-tight">United Youngs India<br />Control Room</h1>
            <p className="mt-4 text-white/50 max-w-sm">Manage projects, enquiries and site content in one place.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-gold text-navy mb-8">
            <Lock className="h-5 w-5" />
          </div>
          <h2 className="font-display text-3xl text-white">Admin sign in</h2>
          <p className="mt-2 text-white/40 text-sm">Enter your credentials to continue.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/40">Email</label>
              <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm bg-white/5 border-white/10 text-white" placeholder="admin@unitedyoungsindia.com" />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/40">Password</label>
              <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm bg-white/5 border-white/10 text-white" placeholder="••••••••" />
            </div>
          </div>

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-8 w-full rounded-sm bg-gold py-3.5 text-navy font-medium hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
